import React, { useEffect, useState, useRef } from 'react';
import { Calendar, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const EventimeUpcoming = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const upcomingEvents = [
    {
      id: 1,
      title: 'Shape Indonesia',
      date: t('upcoming.datePlaceholder'),
      location: 'TBA',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjb25mZXJlbmNlfGVufDB8fHx8MTY4OTUwMzAwMHww&ixlib=rb-4.0.3&q=80&w=1080'
    },
    {
      id: 2,
      title: 'NexCon Indonesia',
      date: t('upcoming.datePlaceholder'),
      location: 'TBA',
      image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjb252ZW50aW9ufGVufDB8fHx8MTY4OTUwMzAwMHww&ixlib=rb-4.0.3&q=80&w=1080'
    }
  ];

  return (
    <section id="upcoming-events" className="section" ref={sectionRef} style={{ background: 'var(--bg-subtle)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-giant)' }}>
          <h2 
            className={`heading-1 animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ marginBottom: 'var(--spacing-medium)' }}
          >
            {t('upcoming.title')}
          </h2>
          
          <p 
            className={`body-large animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ 
              maxWidth: '700px', 
              margin: '0 auto',
              transitionDelay: '0.2s'
            }}
          >
            {t('upcoming.subtitle')}
          </p>
        </div>

        {/* Events Grid */}
        <div 
          className="network-grid"
          style={{ 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: 'var(--spacing-xl)',
            padding: '0'
          }}
        >
          {upcomingEvents.map((event, index) => (
            <div 
              key={event.id}
              className={`network-card hover-lift animated ${isVisible ? 'fadeIn' : ''}`}
              style={{ 
                transitionDelay: `${0.3 + index * 0.1}s`,
                position: 'relative',
                overflow: 'hidden',
                padding: '0'
              }}
            >
              {/* Event Image with Blur */}
              <div style={{
                width: '100%',
                height: '250px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <img 
                  src={event.image}
                  alt={event.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'blur(8px)',
                    transform: 'scale(1.1)'
                  }}
                />
                
                {/* Coming Soon Overlay */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(2px)'
                }}>
                  <div style={{
                    background: 'var(--brand-secondary)',
                    color: 'var(--brand-dark)',
                    padding: '12px 32px',
                    borderRadius: '50px',
                    fontWeight: '700',
                    fontSize: '1.2rem',
                    boxShadow: '0 4px 20px rgba(241, 196, 15, 0.4)',
                    textTransform: 'uppercase',
                    letterSpacing: '2px'
                  }}>
                    {t('upcoming.comingSoon')}
                  </div>
                </div>
              </div>

              {/* Event Details */}
              <div style={{ padding: 'var(--spacing-large)' }}>
                <h3 className="network-card-title" style={{ 
                  marginBottom: 'var(--spacing-medium)',
                  fontSize: '1.8rem'
                }}>
                  {event.title}
                </h3>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-small)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-small)',
                    color: 'var(--text-light)'
                  }}>
                    <Calendar size={18} color="var(--brand-primary)" />
                    <span className="body-medium">{event.date}</span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-small)',
                    color: 'var(--text-light)'
                  }}>
                    <MapPin size={18} color="var(--brand-primary)" />
                    <span className="body-medium">{event.location}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EventimeUpcoming;
